import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, useToastContext } from '@resideo/blueprint-oneportal';
import type { UpdateUserInviteDataModel } from '@resideo/web-integration-services-api-client';
import apiService, { DeviceFilter } from '@/services/apiService';
import type { ConsumerUserWithLocations, ConsumerDeviceItem, ConsumerUserDetailResponse } from '@/services/apiService';
import { RoleDropdown } from './RoleDropdown';
import { useTranslation } from 'react-i18next';
import styles from './ChangeRoleModal.module.css';

type InviteRole = NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['role'];
type ScheduleInfo = NonNullable<NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['scheduleInfo']>;
type WeekDays = ScheduleInfo['weekDays'];

const ALL_WEEK_DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as unknown as WeekDays;

export interface ChangeRoleModalProps {
  currentRole: string;
  email: string;
  consumerAccountId: string;
  invitedUserId: string;
  consumerUserId: string;
  onClose: () => void;
  onSuccess: (newRole: string) => void;
}

export function ChangeRoleModal({
  currentRole,
  email,
  consumerAccountId,
  invitedUserId,
  consumerUserId,
  onClose,
  onSuccess,
}: ChangeRoleModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToastContext();
  const [selectedRole, setSelectedRole] = useState(currentRole);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (selectedRole === currentRole) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

      let inviteDataInfo: NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>;

      if (selectedRole === 'ADMIN') {
        // Site Manager — no deviceIds required
        inviteDataInfo = {
          role: selectedRole as InviteRole,
          sendToEmail: email,
        };
      } else if (currentRole === 'ADMIN') {
        // Downgrading from Admin — send ALL account device IDs so the user
        // retains full device access in their new (lesser) role.
        const cached = queryClient.getQueryData<Awaited<ReturnType<typeof apiService.getConsumerAccounts>>>(
          ['consumerAccountsAll'],
        );
        const accountsData = cached ?? await apiService.getConsumerAccounts(undefined, DeviceFilter.ALL);
        const allConsumerUsers: ConsumerUserWithLocations[] = accountsData?.data?.consumerUsers ?? [];
        const allDeviceIds = allConsumerUsers
          .filter(cu => cu?.consumerAccount?.id === consumerAccountId)
          .flatMap(cu => cu?.consumerAccount?.locations ?? [])
          .flatMap(loc => loc.consumerDevices ?? [] as ConsumerDeviceItem[])
          .map(d => d.device?.deviceId ?? d.id)
          .filter((id): id is string => Boolean(id));

        inviteDataInfo = {
          role: selectedRole as InviteRole,
          sendToEmail: email,
          deviceIds: allDeviceIds,
          ...(selectedRole === 'GUEST' && {
            scheduleInfo: {
              weekDays: ALL_WEEK_DAYS,
              startDate: now.toISOString(),
              endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              startTime: '00:01',
              endTime: '23:59',
            },
          }),
        };
      } else {
        // Non-admin → non-admin role change (e.g. MEMBER → GUEST).
        // Preserve only the device IDs the user currently has access to —
        // do NOT send devices from the full account that they never had.
        const cached = queryClient.getQueryData<ConsumerUserDetailResponse>(
          ['consumerUserDetail', consumerAccountId, consumerUserId],
        );
        const userDetail = cached ?? await apiService.getConsumerUserDetail(consumerAccountId, consumerUserId);
        const currentDeviceIds = (userDetail?.data?.consumerAccount?.locations ?? [])
          .flatMap(loc => loc.consumerDevices ?? [])
          .map(d => d.device?.deviceId ?? d.id)
          .filter((id): id is string => Boolean(id));

        inviteDataInfo = {
          role: selectedRole as InviteRole,
          sendToEmail: email,
          deviceIds: currentDeviceIds,
          ...(selectedRole === 'GUEST' && {
            scheduleInfo: {
              weekDays: ALL_WEEK_DAYS,
              startDate: now.toISOString(),
              endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              startTime: '00:01',
              endTime: '23:59',
            },
          }),
        };
      }

      const body: UpdateUserInviteDataModel = {
        invitedUserId,
        inviteDataInfo,
        emailTemplateInfo: {
          templateLanguage: 'en-US',
          baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
          expiresAt,
        },
      };

      await apiService.updateInvite(consumerAccountId, body);
      addToast({ message: t('changeRoleModal.updateSaved'), toastType: 'Success', zIndex: '9999' });
      onSuccess(selectedRole);
    } catch {
      setError('Failed to update role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="change-role-title">
      <div className={styles.modal}>
        <p className={styles.title} id="change-role-title">{t('changeRoleModal.title')}</p>

        {error && (
          <p className={styles.errorText}>{error}</p>
        )}

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="role-dropdown">Role</label>
          <div className={styles.selectWrapper}>
            <RoleDropdown id="role-dropdown" value={selectedRole} onChange={setSelectedRole} />
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="highlightTertiary" onClick={onClose} disabled={isSaving} style={{ fontWeight: 700 }}>
            {t('employees.resendInviteCancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving} style={{ fontWeight: 700 }}>
            {isSaving ? t('changeRoleModal.saving') : t('changeRoleModal.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
